<template>
  <div class="audit-grid">
    <div
      class="grid-container mt-2"
      :class="{'compact': bottomPanel}"
    >
      <GridHeader
        :gridtitle="$tc('audit.grid.searchResults')"
        :gridsubtitle="msgSelectedRows"
      >
        <grid-display-options
          v-if="gridApi"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />

        <vt-report-export-buttons
          source="audit-grid"
          :export-report="exportReport"
        />
      </GridHeader>

      <vt-grid
        v-testing="'elements'"
        :short-key="{ left: ['ctrl','arrowleft'], right: ['ctrl','arrowright'], close: ['ctrl', 'esc'] }"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        class="audit-grid-results"
        @shortkey.prevent.native="handleKeyboardGridNavigation($event)"
      />
    </div>

    <audit-bottom-panel
      v-if="bottomPanel"
      :audited-action-id="selectedAuditedActionId"
      @closeBottomPanel="closeBottomPanel()"
    />

    <print-component ref="printComponent" />
  </div>
</template>

<script>
import GridHeader from '@/components/GridHeader'
import GridDisplayOptions from '@/components/GridDisplayOptions'
import VtGrid from '@/components/VtGrid/VtGrid'
import { auditGridColumnDefs } from '@/modules/auditModule/views/grid/config/audit-grid.column-defs'
import AuditBottomPanel from '@/modules/auditModule/views/components/audit-bottom-panel/AuditBottomPanel'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { AuditDatasource } from '@/modules/auditModule/views/grid/config/audit-datasource'
import Encoder from '@/utils/encoder'
import AuditedActionService from '@/modules/auditModule/services/AuditedActionService'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { GridUtils } from '@/shared/util/grid-utils'

export default {
    name: 'AuditGrid',
    components: {
        GridHeader,
        VtGrid,
        AuditBottomPanel,
        GridDisplayOptions
    },
    mixins: [
        GridExportMixin
    ],
    data() {
        return {
            bottomPanel: false,
            columnDefs: auditGridColumnDefs,
            pageSize: 20,
            selectedAuditedActionId: null,
            rowStyleOptions: [
                { value: 'compact', icon: 'fal fa-align-justify' },
                { value: 'normal', icon: 'fal fa-bars' }
            ],
            rowStyleSelected: { value: 'compact', icon: 'fal fa-align-justify' },
            currentFilterModel: null,
            columnApi: null,
            gridApi: null,
            encoder: new Encoder(),
            selectedRows: [],
            currentClickedRow: null
        }
    },
    computed: {
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                onRowClicked: this.openBottomPanel,
                paginationPageSize: this.pageSize,
                paginationAutoPageSize: false,
                pagination: true,
                getRowHeight: () => this.rowHeight,
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true // remove number in Group Column
                    }
                }
            }
        },
        rowHeight() {
            if (this.rowStyleSelected.value === 'compact') {
                return 25
            }
            return 40
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        }
    },
    beforeDestroy() {
        this.$eventHub.$off('updatedFilterModelForAuditGrid', this.applyFilterModel)
    },
    mounted() {
        this.$eventHub.$on('updatedFilterModelForAuditGrid', this.applyFilterModel)
    },
    methods: {
        handleKeyboardGridNavigation(event) {
            let nextRow = null
            if (this.currentClickedRow) {
                switch (event.srcKey) {
                case 'right':
                    nextRow = null
                    this.gridApi.forEachNode(node => {
                        if (node.rowIndex === (this.currentClickedRow.rowIndex + 1)) {
                            nextRow = node
                            this.gridApi.setFocusedCell(node.rowIndex, node.colKey)
                        }
                    })
                    this.openBottomPanel(nextRow)
                    break
                case 'left':
                    nextRow = null
                    this.gridApi.forEachNode(node => {
                        if (node.rowIndex === (this.currentClickedRow.rowIndex - 1)) {
                            nextRow = node
                            this.gridApi.setFocusedCell(node.rowIndex, node.colKey)
                        }
                    })
                    this.openBottomPanel(nextRow)
                    break
                case 'close':
                    this.closeBottomPanel()
                    break
                }
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        closeBottomPanel() {
            this.bottomPanel = false
            this.selectedAuditedActionId = null
        },
        openBottomPanel(node) {
            if (node?.data) {
                this.selectedAuditedActionId = node.data.id
                this.currentClickedRow = node
                this.$nextTick(() => { this.bottomPanel = true })
            }
        },
        onGridReady(event) {
            this.gridApi = event.api
            this.columnApi = event.columnApi
        },
        reloadRowData() {
            this.closeBottomPanel()
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new AuditDatasource(this.currentFilterModel))
        },
        applyFilterModel(filterModel) {
            if (!this.gridApi) {
                setTimeout(() => this.applyFilterModel(filterModel), 200)
                return
            }
            this.currentFilterModel = filterModel
            this.reloadRowData()
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.rowStyleSelected = selectedOption
            this.reloadRowData()
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                return AuditedActionService.exportTo(
                    this.$options.name,
                    this.encoder.parseBooleans(this.currentFilterModel),
                    GridUtils.getSortValue(this.gridApi.getSortModel()),
                    columns,
                    this.selectedRows.map(selectedRow => selectedRow.id),
                    exportType)
            }
            return null
        },
        formatNumber(value) {
            return value.toLocaleString()
        }
    }
}
</script>

<style lang="scss">

    .audit-grid .grid-container.compact {
        margin-bottom: 10px;
    }

.audit-grid .sr-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem;
  flex-shrink: 0;
}

.audit-grid .sr-header-buttons {
  margin-right: 1.5em;
}

.audit-grid .bottom-panel{
    background:#FFF;
    box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.1);
}

.audit-grid-results {
    height: 100%;
}

.audit-grid .audit-detail-grid{
  height: 100%;
}

</style>
